"use client";

import cn from "@utils/cn";
import { showMicroComponentsFeatureFlag } from "@utils/default";
import { transparentPixel } from "@utils/string";
import { deleteCookie, setCookie } from "cookies-next";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import CurrencyIcon from "@/components/ui/CurrencyIcon/CurrencyIcon";
import Icon from "@/components/ui/Icon/Icon";
import { LoadingContext } from "@/context/LoaderContext";
import { ModalsContext } from "@/context/ModalsContext";
import { UserContext } from "@/context/UserContext";
import { ecomInitiateCheckout, entryPointCtaClick } from "@/dataLayer/dataLayer";
import { projectPageViewEvent } from "@/dataLayer/dataLayer";
import { brochureDowloadCtaClick, ctaClickTracking } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";

import { MicrocopyComponent } from "../AgentChatBot/MicroComponent";
import ButtonOrVideoModal from "../ui/ButtonOrVideoModal/ButtonOrVideoModal";
import { EventLaunchType, StickyBarProps } from "./types";

export default function StickyBar({
  communityLogo,
  unitBedroom,
  projectTypes,
  projectPrice,
  status,
  productId,
  sellable,
  projectCity,
  projectName,
  title,
  description,
  isProject,
  projectStatus,
  typesProject,
  serverSideCookieCurrency,
  currencyFromApiRes,
  fallbackTitle,
  fallbackDescription,
  leadFormPageLocation,
  isInventoryAvaialable,
  videocontent,
  projectdatalayer,
  projectData,
  projectPDF,
  microcopyComponent,
  availability,
}: StickyBarProps) {
  const t = useTranslations();
  const format = useFormatter();
  const router = useRouter();
  const isSo = useIsSaleScreen();
  const locale = useLocale();
  const { currency } = useContext(UserContext);
  const {
    setNotifyMeModalOpen,
    setEnquireModalOpen,
    setAdditionalInfo,
    additionalInfo,
    setGetFormPageLocation,
    setMetaverseWalkthroughModalOpen,
    settings,
    setDownloadBrochureModalOpen,
  } = useContext(ModalsContext);
  const { loading } = useContext(LoadingContext);
  const [isLoadingForPrice, setIsLoadingForPrice] = useState(true);
  const [isMicroComponentOpen, setIsMicroComponentOpen] = useState(false);
  const resetMicrocopyState = () => {
    setIsMicroComponentOpen(false);
  };

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    trailingZeroDisplay: "stripIfInteger",
    currencyDisplay: locale?.split("-")[1] === "ae" ? "code" : "symbol",
  })
    .format(Math.round(Number(projectPrice) / 100) || 0)
    ?.replace(/(\p{Sc})(\d)/u, "$1 $2")
    ?.replaceAll("UK", "");

  const setSessionData = (data: Record<string, string>) => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("stickyBarData", JSON.stringify(data));
    }
  };

  useEffect(() => {
    if (serverSideCookieCurrency && currencyFromApiRes && currency) {
      if (serverSideCookieCurrency === currencyFromApiRes && currencyFromApiRes === currency) {
        setIsLoadingForPrice(false);
      } else {
        setIsLoadingForPrice(true);
      }
    }
  }, [serverSideCookieCurrency, currencyFromApiRes, currency]);

  const bedroomsValue = useMemo(() => {
    if (unitBedroom) {
      if (unitBedroom.min != unitBedroom.max) {
        return `${unitBedroom.min}-${unitBedroom.max}`; //${t("bedrooms.label")}
      } else {
        return `${unitBedroom.min}`; //${t("bedrooms.label")}
      }
    }
    return "";
  }, [t, unitBedroom]);

  useEffect(() => {
    setAdditionalInfo({
      property_category: typesProject,
      property_indicative_price: formattedPrice == "Â£0" ? "" : (formattedPrice as string),
      project_name: projectName,
      project_city: projectCity,
      project_type: typesProject,
      bedrooms_number: bedroomsValue == "0" ? "" : (bedroomsValue as string),
      project_status: projectStatus,
    });
  }, [bedroomsValue, projectCity, projectName, formattedPrice, projectTypes, setAdditionalInfo, projectStatus, typesProject]);
  // const fromLabel = useResponsiveLabel(t("stickybar.mobile.from"), { 0: formattedPrice }, t("stickybar.desktop.from"), { 0: formattedPrice });

  // useEffect(() => {
  //   if (projectdatalayer && projectName && typesProject) {
  //     projectPageViewEvent(projectName, typesProject);
  //   }
  // }, []);

  const fromLabel = useResponsiveLabel(
    `${t("stickybar.mobile.from")} ${isLoadingForPrice ? t("common.loading") : formattedPrice}`,
    `${t("stickybar.desktop.from")} ${isLoadingForPrice ? t("common.loading") : formattedPrice} `,
  );

  const comingSoonTitle = useResponsiveLabel(t("stickybar.mobile.comingSoonTitle"), t("stickybar.desktop.comingSoonTitle"));
  const comingSoonDescription = useResponsiveLabel(t("stickybar.mobile.comingSoonDescription"), t("stickybar.desktop.comingSoonDescription"));
  const keepMeUpdatedLabel = useResponsiveLabel(t("stickybar.mobile.keepMeUpdated"), t("stickybar.desktop.keepMeUpdated"));
  const enquireLabel = useResponsiveLabel(t("stickybar.mobile.enquire"), t("stickybar.desktop.enquire"));
  const selectUnitLabel = useResponsiveLabel(t("stickybar.mobile.selectUnit"), t("stickybar.desktop.selectUnit"));
  const notifyOfAvailaibilitiesLabel = useResponsiveLabel(
    t("stickybar.mobile.notifyOfAvailaibilities"),
    t("stickybar.desktop.notifyOfAvailaibilities"),
  );
  const payExpressionOfInterestLabel = useResponsiveLabel(
    t("stickybar.mobile.payExpressionOfInterest"),
    t("stickybar.desktop.payExpressionOfInterest"),
  );
  console.log("showMicroComponentsFeatureFlag", showMicroComponentsFeatureFlag);
  const selectOnMapLabel = useResponsiveLabel(t("stickybar.mobile.selectOnMap"), t("stickybar.desktop.selectOnMap"));
  const registerInterestLabel = useResponsiveLabel(t("stickybar.mobile.registerInterest"), t("stickybar.desktop.registerInterest"));
  const reachUsForGuidanceLabel = useResponsiveLabel(t("stickybar.mobile.reachUsForGuidance"), t("stickybar.desktop.reachUsForGuidance"));

  const isThereProjectData = unitBedroom && (unitBedroom.min || unitBedroom.max) && projectTypes;
  // teaser, reveal and sold out status allows user to customize title and description into stickybar
  const isCustomizableFieldStatus = status === EventLaunchType.TEASER || status === EventLaunchType.REVEAL || status === EventLaunchType.SOLD_OUT;

  let showProjectData = !isProject || (isThereProjectData && !isCustomizableFieldStatus);
  if (!status) return;
  return (
    <div className="md:block">
      {microcopyComponent && isMicroComponentOpen && (
        <MicrocopyComponent {...{ ...microcopyComponent, isStickybar: true }} resetMicrocopyState={resetMicrocopyState} />
      )}
      <section className="fixed z-10 left-0 right-0 top-auto flex items-center mx-auto rounded-full bottom-m  gap-xs p-[3px] box-material-tertiary backdrop-blur-md w-22\/24 md:w-20\/24 md:bottom-m md:pr-xxs md:rtl:pl-xxs overflow-hidden mt-0 mb-0">
        <div className="hidden md:flex md:items-center">
          {showProjectData ? (
            <>
              <Image
                src={communityLogo.src || transparentPixel}
                alt={communityLogo.alt || ""}
                width="70"
                height="40"
                className="w-[70px] h-[40px] mx-l brightness-0"
              />
              <div className="flex flex-col text-black uppercase gap-xs font-primary">
                {unitBedroom ? (
                  <p className="font-medium text-s">
                    {unitBedroom.min != unitBedroom.max ? (
                      <>
                        {unitBedroom.min}-{unitBedroom.max}
                        <span style={{ padding: "8px" }}>{t("bedrooms.label")}</span>
                        {projectTypes
                          ? projectTypes?.toLowerCase() === "apartment"
                            ? t("propertytype.label.apartment")
                            : projectTypes?.toLowerCase() === "penthouse"
                              ? t("propertytype.label.penthouse")
                              : t("propertytype.label.villa")
                          : ""}
                      </>
                    ) : (
                      // {t("view.more")}
                      <>
                        {unitBedroom.min} <span style={{ padding: "8px" }}>{t("bedrooms.label")}</span>
                        {projectTypes
                          ? projectTypes?.toLowerCase() === "apartment"
                            ? t("propertytype.label.apartment")
                            : projectTypes?.toLowerCase() === "penthouse"
                              ? t("propertytype.label.penthouse")
                              : t("propertytype.label.villa")
                          : ""}
                      </>
                    )}
                  </p>
                ) : (
                  status === EventLaunchType.COMMUNITY && !unitBedroom && <p className="font-medium text-s">{fallbackTitle}</p>
                )}

                {status !== EventLaunchType.SOLD_OUT &&
                  status !== EventLaunchType.POST_LAUNCH &&
                  formattedPrice &&
                  (projectPrice ? (
                    <p className="text-xs">
                      {/* {fromLabel} */}
                      {(() => {
                        let labelText: any = fromLabel as unknown;
                        if (typeof fromLabel === "object" && fromLabel !== null && "props" in fromLabel && (fromLabel as any)?.props?.children) {
                          labelText = (fromLabel as any)?.props?.children as string;
                        }
                        if (typeof labelText === "string" && labelText.includes("AED")) {
                          const [fromPart, ...rest] = labelText.split("AED");
                          return (
                            <>
                              {fromPart}
                              <CurrencyIcon color="#343434" /> {rest.join("").trim()}
                            </>
                          );
                        }
                        return fromLabel;
                      })()}
                    </p>
                  ) : (
                    EventLaunchType.COMMUNITY === status && !projectPrice && <p className="text-xs">{fallbackDescription}</p>
                  ))}
                {status === EventLaunchType.POST_LAUNCH && (
                  <div className="flex flex-col text-black uppercase font-primary">
                    {!showProjectData && <p className="font-medium text-s">{title || comingSoonTitle}</p>}
                    <p className="text-xs">
                      {showProjectData
                        ? // fromLabel
                          (() => {
                            let labelText: any = fromLabel as unknown;
                            if (typeof fromLabel === "object" && fromLabel !== null && "props" in fromLabel && (fromLabel as any)?.props?.children) {
                              labelText = (fromLabel as any)?.props?.children as string;
                            }
                            if (typeof labelText === "string" && labelText.includes("AED")) {
                              const [fromPart, ...rest] = labelText.split("AED");
                              return (
                                <>
                                  {fromPart}
                                  <CurrencyIcon color="#343434" /> {rest.join("").trim()}
                                </>
                              );
                            }
                            return fromLabel;
                          })()
                        : description || comingSoonDescription}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {!showProjectData && status !== EventLaunchType.SOLD_OUT && (
                <Image
                  src={communityLogo.src || transparentPixel}
                  alt={communityLogo.alt || ""}
                  width="70"
                  height="40"
                  className="w-[70px] h-[40px] mx-l"
                />
              )}
              {status === EventLaunchType.SOLD_OUT && (
                <Image
                  src={communityLogo.src || transparentPixel}
                  alt={communityLogo.alt || ""}
                  width="70"
                  height="40"
                  className="w-[70px] h-[40px] mx-l"
                />
              )}
              {status === EventLaunchType.POST_LAUNCH || status === EventLaunchType.LAUNCH ? (
                <div className="flex flex-col text-black uppercase font-primary">
                  <p className="font-medium text-s">{t("inventoryIsNotAvailable")}</p>
                </div>
              ) : (
                <div className="flex flex-col text-black uppercase font-primary">
                  <p className="font-medium text-s">{title || comingSoonTitle}</p>
                </div>
              )}
            </>
          )}
        </div>

        {isSo ? (
          <div className="flex ml-auto rtl:ml-0 rtl:mr-auto gap-xs w-full md:w-auto">
            {projectPDF && (
              <Button
                size={ButtonSize.MEDIUM}
                customClass="w-full md:w-fit group"
                theme={ButtonTheme.BLACK}
                type="button"
                // variant={ButtonVariant.PRIMARY}
                variant={ButtonVariant.SECONDARY}
                onClick={() => {
                  brochureDowloadCtaClick({ cta_name: t("label.brochure"), ...additionalInfo });
                  settings.brochureData = { url: projectPDF, projectData };
                  setDownloadBrochureModalOpen(true);
                }}
              >
                <span className="flex items-center uppercase gap-xs font-medium group-hover:text-black">
                  <Icon className="stroke-black group-hover:stroke-black transition-colors duration-200" name="download" size="20" />
                  {t("label.brochure")}
                </span>
              </Button>
            )}
            {isInventoryAvaialable && videocontent && (
              <ButtonOrVideoModal
                cta={videocontent?.primaryCta}
                btnClasses={cn(
                  "inline-flex border-none justify-center md:mx-auto btn-primary-black whitespace-nowrap",
                  videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-black shadow-strong",
                  videocontent?.secondaryCta && "w-full",
                  "md:mx-auto",
                )}
                btnSize={ButtonSize.MEDIUM}
              />
            )}
            {!availability && isInventoryAvaialable && !videocontent && (
              <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.BLACK}
                customClass="w-full md:w-fit"
                onClick={() => {
                  const AULComponent = document.body.querySelector("#APL-component");
                  if (AULComponent) {
                    const rect = AULComponent.getBoundingClientRect();
                    window.scrollTo({
                      top: rect.top + window.scrollY,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                {selectUnitLabel}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex ml-auto rtl:ml-0 rtl:mr-auto gap-xs w-full md:w-auto">
            {/* TEASER  */}
            {status === EventLaunchType.TEASER && (
              <>
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    entryPointCtaClick({ cta_name: t("stickybar.mobile.notifyOfAvailaibilities"), ...additionalInfo });
                    setNotifyMeModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {keepMeUpdatedLabel}
                </Button>
              </>
            )}
            {/* REVEAL  */}
            {status === EventLaunchType.REVEAL && (
              <>
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.SECONDARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    entryPointCtaClick({ cta_name: t("stickybar.mobile.notifyOfAvailaibilities"), ...additionalInfo });
                    setNotifyMeModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {notifyOfAvailaibilitiesLabel}
                </Button>

                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    if (projectName) {
                      setSessionData({ projectName });
                    }
                    if (sellable) {
                      status === EventLaunchType.REVEAL &&
                        entryPointCtaClick({
                          cta_name: payExpressionOfInterestLabel?.props?.children,
                          // cta_name: t("stickybar.mobile.payExpressionOfInterest"),
                          ...additionalInfo,
                        });
                      loading(true);
                      setCookie("eoiID", productId);
                      ecomInitiateCheckout({
                        cta_name: payExpressionOfInterestLabel?.props?.children ?? "",
                        form_name: "prebook_online",
                        ...additionalInfo,
                      });

                      deleteCookie("reserveID");
                      // router.push("/checkout");
                      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
                    } else {
                      // status === EventLaunchType.REVEAL && entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                      status === EventLaunchType.REVEAL && ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                      setEnquireModalOpen(true);
                      setGetFormPageLocation(leadFormPageLocation);
                    }
                  }}
                >
                  {sellable ? (
                    <>
                      {payExpressionOfInterestLabel}
                      {showMicroComponentsFeatureFlag && microcopyComponent && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMicroComponentOpen(true);
                          }}
                          className="pl-2"
                        >
                          <Icon name="info.svg" size="16" />
                        </span>
                      )}
                    </>
                  ) : (
                    enquireLabel
                  )}
                </Button>
              </>
            )}
            {/* LAUNCH */}
            {status === EventLaunchType.LAUNCH && (
              <>
                {" "}
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={showProjectData ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    if (projectName) {
                      setSessionData({ projectName });
                    }
                    // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                    ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                    setEnquireModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {enquireLabel}
                </Button>
                {isInventoryAvaialable && videocontent ? (
                  <ButtonOrVideoModal
                    cta={videocontent?.primaryCta}
                    btnClasses={cn(
                      "inline-flex border-none justify-center md:mx-auto btn-primary-black whitespace-nowrap",
                      videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-black shadow-strong",
                      videocontent?.secondaryCta && "w-full",
                      "md:mx-auto",
                    )}
                    btnSize={ButtonSize.MEDIUM}
                    isMetaverseCta
                  />
                ) : (
                  showProjectData &&
                  (!isInventoryAvaialable ? (
                    <Button
                      size={ButtonSize.MEDIUM}
                      variant={ButtonVariant.PRIMARY}
                      theme={ButtonTheme.BLACK}
                      customClass="w-full md:w-fit"
                      onClick={() => {
                        if (projectName) {
                          setSessionData({ projectName });
                        }
                        // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                        // ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                        setEnquireModalOpen(true);
                        setGetFormPageLocation(leadFormPageLocation);
                      }}
                    >
                      {registerInterestLabel}
                    </Button>
                  ) : (
                    <Button
                      size={ButtonSize.MEDIUM}
                      variant={ButtonVariant.PRIMARY}
                      theme={ButtonTheme.BLACK}
                      customClass="w-full md:w-fit"
                      onClick={() => {
                        const AULComponent = document.body.querySelector("#APL-component");
                        if (AULComponent) {
                          const rect = AULComponent.getBoundingClientRect();
                          window.scrollTo({
                            top: rect.top + window.scrollY,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      {selectUnitLabel}
                    </Button>
                  ))
                )}
              </>
            )}

            {/* SOULD OUT */}
            {status === EventLaunchType.SOLD_OUT && (
              <>
                {isSo && isInventoryAvaialable && videocontent && (
                  <ButtonOrVideoModal
                    cta={videocontent?.primaryCta}
                    btnClasses={cn(
                      "inline-flex border-none justify-center md:mx-auto btn-primary-black whitespace-nowrap",
                      videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-black shadow-strong",
                      videocontent?.secondaryCta && "w-full",
                      "md:mx-auto",
                    )}
                    btnSize={ButtonSize.MEDIUM}
                  />
                )}
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    if (projectName) {
                      setSessionData({ projectName });
                    }
                    setEnquireModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {reachUsForGuidanceLabel}
                </Button>
              </>
            )}
            {/* COMMUNITY  */}
            {status === EventLaunchType.COMMUNITY && (
              <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.BLACK}
                customClass="w-full md:w-fit"
                onClick={() => {
                  if (projectName) {
                    setSessionData({ projectName });
                  }
                  // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                  ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                  setEnquireModalOpen(true);
                  setGetFormPageLocation(leadFormPageLocation);
                }}
              >
                {enquireLabel}
              </Button>
            )}

            {/*  RESERVED or RESTRICTED */}
            {(status === EventLaunchType.RESERVED || status === EventLaunchType.RESTRICTED) && (
              <>
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.SECONDARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    entryPointCtaClick({ cta_name: t("stickybar.mobile.notifyOfAvailaibilities"), ...additionalInfo });
                    setNotifyMeModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {notifyOfAvailaibilitiesLabel}
                </Button>

                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.PRIMARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    if (projectName) {
                      setSessionData({ projectName });
                    }
                    if (sellable) {
                      entryPointCtaClick({ cta_name: t("stickybar.mobile.payExpressionOfInterest"), ...additionalInfo });
                      loading(true);
                      setCookie("eoiID", productId);
                      ecomInitiateCheckout({
                        cta_name: payExpressionOfInterestLabel?.props?.children ?? "",
                        ...additionalInfo,
                      });

                      deleteCookie("reserveID");
                      // router.push("/checkout");
                      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
                    } else {
                      // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                      ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                      setEnquireModalOpen(true);
                      setGetFormPageLocation(leadFormPageLocation);
                    }
                  }}
                >
                  {sellable ? payExpressionOfInterestLabel : enquireLabel}
                </Button>
              </>
            )}

            {/* POST LAUNCH */}
            {status === EventLaunchType.POST_LAUNCH && (
              <>
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={!showProjectData ? ButtonVariant.PRIMARY : ButtonVariant.SECONDARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    if (projectName) {
                      setSessionData({ projectName });
                    }
                    // entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                    ctaClickTracking(t("stickybar.mobile.enquire"), "StickyBar", "StickyBar", true);
                    setEnquireModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {enquireLabel}
                </Button>

                {/* if no 3D map select unit */}
                {/* {showProjectData && (
              <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.BLACK}
                customClass="w-full md:w-fit"
                onClick={() => {
                  const AULComponent = document.body.querySelector("#APL-component");
                  if (AULComponent) {
                    const rect = AULComponent.getBoundingClientRect();
                    window.scrollTo({
                      top: rect.top + window.scrollY,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                {selectUnitLabel}
              </Button>
            )} */}

                {isInventoryAvaialable && videocontent ? (
                  <ButtonOrVideoModal
                    cta={videocontent?.primaryCta}
                    btnClasses={cn(
                      "inline-flex border-none justify-center md:mx-auto btn-primary-black whitespace-nowrap",
                      videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-black shadow-strong",
                      videocontent?.secondaryCta && "w-full",
                      "md:mx-auto",
                    )}
                    btnSize={ButtonSize.MEDIUM}
                    isMetaverseCta
                  />
                ) : (
                  showProjectData && (
                    <Button
                      size={ButtonSize.MEDIUM}
                      variant={ButtonVariant.PRIMARY}
                      theme={ButtonTheme.BLACK}
                      customClass="w-full md:w-fit"
                      onClick={() => {
                        const AULComponent = document.body.querySelector("#APL-component");
                        if (AULComponent) {
                          const rect = AULComponent.getBoundingClientRect();
                          window.scrollTo({
                            top: rect.top + window.scrollY,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      {selectUnitLabel}
                    </Button>
                  )
                )}

                {/* {  isInventoryAvaialable &&
  videocontent ? (            <ButtonOrVideoModal
              cta={videocontent?.primaryCta}
              btnClasses={cn(
                "mt-[40px] md:mt-l inline-flex border-none justify-center md:mx-auto btn-primary-white whitespace-nowrap",
                videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-white shadow-strong",
                videocontent?.secondaryCta && "w-full",
                "md:mx-auto",
              )}
              btnSize={ButtonSize.MEDIUM}
            />):()} */}

                {/* <Button size={ButtonSize.MEDIUM} variant={ButtonVariant.PRIMARY} theme={ButtonTheme.BLACK} customClass="w-full md:w-fit">
              {selectOnMapLabel}
            </Button> */}
              </>
            )}

            {/* COMMUNITY 
          {status === EventLaunchType.COMMUNITY && (
            <Button
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.PRIMARY}
              theme={ButtonTheme.BLACK}
              customClass="w-full md:w-fit"
              onClick={() => {
                if (projectName) {
                  setSessionData({ projectName });
                }
                entryPointCtaClick({ cta_name: t("stickybar.mobile.enquire"), ...additionalInfo });
                setEnquireModalOpen(true);
                setGetFormPageLocation(leadFormPageLocation);
              }}
            >
              {enquireLabel}
            </Button>
          )} */}

            {/*  RESERVED or RESTRICTED */}
            {(status === EventLaunchType.RESERVED || status === EventLaunchType.RESTRICTED) && (
              <>
                <Button
                  size={ButtonSize.MEDIUM}
                  variant={ButtonVariant.SECONDARY}
                  theme={ButtonTheme.BLACK}
                  customClass="w-full md:w-fit"
                  onClick={() => {
                    entryPointCtaClick({ cta_name: t("stickybar.mobile.notifyOfAvailaibilities"), ...additionalInfo });
                    setNotifyMeModalOpen(true);
                    setGetFormPageLocation(leadFormPageLocation);
                  }}
                >
                  {notifyOfAvailaibilitiesLabel}
                </Button>

                {isInventoryAvaialable && videocontent ? (
                  <ButtonOrVideoModal
                    cta={videocontent?.primaryCta}
                    btnClasses={cn(
                      "inline-flex border-none justify-center md:mx-auto btn-primary-black whitespace-nowrap",
                      videocontent?.primaryCta?.type !== "videoModal" && "btn btn-medium btn-primary-black shadow-strong",
                      videocontent?.secondaryCta && "w-full",
                      "md:mx-auto",
                    )}
                    btnSize={ButtonSize.MEDIUM}
                    isMetaverseCta
                  />
                ) : (
                  <Button
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.BLACK}
                    customClass="w-full md:w-fit"
                    onClick={() => {
                      entryPointCtaClick({ cta_name: t("stickybar.mobile.registerInterest"), ...additionalInfo });
                      setEnquireModalOpen(true);
                      setGetFormPageLocation(leadFormPageLocation);
                    }}
                  >
                    {registerInterestLabel}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
